/**
 * useServicesAndBanners hook
 */

// Libraries
import { useSetRecoilState } from 'recoil';

// Recoil
import { isLoading } from 'recoil/LoadingRecoil';
import { DataUserInfoAtom, userInfoAtom } from 'recoil/UserInfoRecoil';

// Services
import AuthService from 'services/AuthService';
import BannerService from 'services/BannerService';

// Hooks
import useLocalStorage from 'hooks/useLocalStorage';

// Utils
import { FORM_BUTTON, STATUS_SERVICE_SYSTEM, STORAGE_KEYS } from 'utils/constants';
import { ServiceSystem } from 'utils/types/serviceType';
import { BannerType } from 'utils/types';

const useServicesAndBanners = (): {
  banners: BannerType[];
  services: ServiceSystem[];
  fetchServiceAndBanners: (_isAuth: boolean) => Promise<DataUserInfoAtom | undefined>;
} => {
  const setUserInfo = useSetRecoilState(userInfoAtom);
  const setIsLoading = useSetRecoilState(isLoading);
  const [banners, setBanners] = useLocalStorage<BannerType[]>(STORAGE_KEYS.BANNERS);
  const [services, setServices] = useLocalStorage<ServiceSystem[]>(STORAGE_KEYS.SERVICE_SYSTEMS);

  const statusTextMap: { [key: string]: string } = {
    [STATUS_SERVICE_SYSTEM.REQUESTED]: FORM_BUTTON.CURRENTLY_APPLYING,
    [STATUS_SERVICE_SYSTEM.IN_USED]: FORM_BUTTON.START_UP,
    [STATUS_SERVICE_SYSTEM.CANCELED]: FORM_BUTTON.REGISTER_SERVICE,
  };

  const processServices = (services: ServiceSystem[], userServices: any[]) => {
    return services.map((service) => {
      const status = userServices?.find((s) => s?.id === service.id)?.status;
      return {
        ...service,
        disabled: status === STATUS_SERVICE_SYSTEM.REQUESTED,
        statusText: statusTextMap[status] || FORM_BUTTON.REGISTER_SERVICE,
      };
    });
  };

  const fetchServices = async (isAuth: boolean): Promise<void> => {
    try {
      setIsLoading(true);

      if (isAuth) {
        const [services, user] = await Promise.all([
          AuthService.getServices(),
          AuthService.getUserInfo(),
        ]);

        setUserInfo(user);
        setServices(processServices(services, user?.services));

        return user;
      }

      const services = await AuthService.getServices();
      setServices(services);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBanners = async (): Promise<void> => {
    try {
      const res = await BannerService.getBanners();
      const bannersForPC = res?.pc || [];
      const bannersForSP = res?.sp || [];

      const banners: BannerType[] = bannersForPC.map((pcBanner: any) => {
        const spBanner = bannersForSP.find(
          (spBanner: any) => spBanner?.position === pcBanner?.position
        );
        return {
          desktop: pcBanner,
          mobile: spBanner,
        };
      });
      setBanners(banners);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchServiceAndBanners = async (_isAuth: boolean): Promise<any> => {
    const [user] = await Promise.all([fetchServices(_isAuth), fetchBanners()]);
    return user;
  };

  return { banners, services, fetchServiceAndBanners };
};

export default useServicesAndBanners;
